import { useGetPlatformDatabaseUsage, type TableSizeOut } from '@myrentalmate/api-client';
import { Alert, Button, Chip, LinearProgress, Paper, Stack, Typography } from '@mui/material';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { StatRow, StatTile } from '../../components/Stats';
import { okData } from '../../lib/apiData';

const WARN_AT = 60;
const PROBLEM_AT = 85;

const color = (percent: number) =>
  percent >= PROBLEM_AT ? 'error' : percent >= WARN_AT ? 'warning' : 'success';

const percent = (value: number | null | undefined) => (value == null ? '–' : `${value}%`);

function Meter({
  label,
  average,
  peak,
}: {
  label: string;
  average?: number | null;
  peak?: number | null;
}) {
  return (
    <Stack spacing={0.5}>
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <Typography>{label}</Typography>
        <Typography variant="body2" color="text.secondary">
          Average {percent(average)}, Peak {percent(peak)}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={Math.min(average ?? 0, 100)}
        color={color(peak ?? 0)}
        aria-label={`${label} average`}
      />
    </Stack>
  );
}

const TABLE_COLUMNS: DataColumn<TableSizeOut>[] = [
  { label: 'Table', render: (t) => t.name },
  { label: 'Rows', align: 'right', render: (t) => t.rows.toLocaleString() },
  { label: 'Size', align: 'right', render: (t) => `${t.size_mb} MB` },
];

export function DatabaseUsagePage() {
  const query = useGetPlatformDatabaseUsage();
  const usage = okData(query.data);
  if (!usage) return null;
  if (!usage.available) {
    return (
      <Alert severity="info">
        Usage isn&apos;t available yet. It appears once this release&apos;s database update has run.
      </Alert>
    );
  }
  const peak = usage.peak_percent ?? 0;
  const flatRate = usage.service_objective?.startsWith('S');
  return (
    <Stack spacing={2}>
      {peak >= PROBLEM_AT ? (
        <Alert severity="error">
          The database reached {peak}% of its limit in the last hour, so pages may be slow. If a job
          is running, you can stop it under Scheduled Jobs.
        </Alert>
      ) : (
        <Alert severity="success">The database has room to spare over the last hour.</Alert>
      )}
      <StatRow>
        <StatTile label="Average, Last Hour" value={percent(usage.average_percent)} />
        <StatTile label="Peak, Last Hour" value={percent(usage.peak_percent)} />
        <StatTile
          label="Database Size"
          value={`${usage.size_mb} MB`}
          hint={usage.max_size_mb ? `Of ${(usage.max_size_mb / 1024).toFixed(0)} GB` : undefined}
        />
        <StatTile
          label="Plan"
          value={usage.service_objective}
          hint={usage.dtu_limit ? `${usage.dtu_limit} DTU` : undefined}
        />
      </StatRow>
      {flatRate && (
        <Typography variant="body2" color="text.secondary">
          This plan has a fixed monthly price. Heavy use makes the database slower, not more
          expensive.
        </Typography>
      )}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Usage Over the Last Hour</Typography>
            <Button size="small" disabled={query.isFetching} onClick={() => void query.refetch()}>
              Refresh
            </Button>
          </Stack>
          <Meter label="Overall" average={usage.average_percent} peak={usage.peak_percent} />
          <Meter
            label="Processor"
            average={usage.cpu_average_percent}
            peak={usage.cpu_peak_percent}
          />
          <Meter
            label="Data Reads"
            average={usage.data_average_percent}
            peak={usage.data_peak_percent}
          />
          <Meter
            label="Data Writes"
            average={usage.log_average_percent}
            peak={usage.log_peak_percent}
          />
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Chip size="small" color="success" label="Under 60%" />
            <Chip size="small" color="warning" label="60% to 85%" />
            <Chip size="small" color="error" label="Over 85%" />
          </Stack>
        </Stack>
      </Paper>
      <Stack spacing={1}>
        <Typography variant="h6">Largest Tables</Typography>
        <DataTable columns={TABLE_COLUMNS} rows={usage.largest_tables} rowKey={(t) => t.name} />
      </Stack>
    </Stack>
  );
}
